import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.ts";
import { canClearUp, emptyAnswer, withDeadline } from "../lib/requests.ts";

// High-level numbers for an admin. Everything is computed in the database by
// two security-invoker views, so what gets counted is whatever the caller's own
// policies let them read: every session for an admin, and nothing extra for
// anyone else even if they reached this screen.
//
// Voided sessions, voided rows and still-open blocks are excluded there, not
// here. An open block has no duration yet, and a voided row is not a measurement.

interface VenueStat {
  venue_id: string; venue_name: string; city: string | null;
  sessions: number; observers: number; observed_seconds: number;
  blocks: number; tables_counted: number; last_observed_at: number | null;
}
interface CategoryStat {
  category_id: string; label: string; ordinal: number; counts_tables: boolean;
  seconds: number; blocks: number; tables_counted: number;
}

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

const when = (ms: number | null) =>
  ms === null ? "Never" : new Date(ms).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });

export default function Stats() {
  const [venues, setVenues] = useState<VenueStat[] | null>(null);
  const [cats, setCats] = useState<CategoryStat[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // The message shown for a failed load. One that can clear up on its own (no
  // signal, a stall, a sign-in that could not be renewed) gets the plain words;
  // a refusal keeps the database's own.
  const failed = (e: { message: string; code?: string }, status?: number) =>
    setErr(canClearUp(e, status) ? "Could not load the numbers. Check your signal and try again." : e.message);

  async function load() {
    setErr(null);
    // Cut off at 15 s, the sign-in renewal included: a stalled renewal used to
    // hold this screen on Loading... with nothing to press.
    const [v, c] = await Promise.all([
      withDeadline((signal) => supabase.from("venue_stats").select("*")
        .order("observed_seconds", { ascending: false }).abortSignal(signal)),
      withDeadline((signal) => supabase.from("category_stats").select("*").order("ordinal").abortSignal(signal)),
    ]);
    if (v.error) { failed(v.error, v.status); return; }
    if (c.error) { failed(c.error, c.status); return; }
    // A success with no body is not zero sessions.
    if (v.data == null || c.data == null) { failed(emptyAnswer); return; }
    // numeric and bigint aggregates can arrive as strings; coerce once, here.
    setVenues(v.data.map((r) => ({
      ...r, sessions: Number(r.sessions), observers: Number(r.observers),
      observed_seconds: Number(r.observed_seconds), blocks: Number(r.blocks),
      tables_counted: Number(r.tables_counted),
      last_observed_at: r.last_observed_at === null ? null : Number(r.last_observed_at),
    })));
    setCats(c.data.map((r) => ({
      ...r, seconds: Number(r.seconds), blocks: Number(r.blocks), tables_counted: Number(r.tables_counted),
    })));
  }
  // A thrown request (no signal at all) lands in err too, rather than
  // leaving the screen on Loading... for good.
  const reload = () => { load().catch((e: unknown) => failed({ message: e instanceof Error ? e.message : String(e) })); };
  useEffect(reload, []);

  // A failed load used to be a dead end with no way back but leaving the tab
  // (BUG-7).
  if (err) {
    return (
      <div className="pad">
        <h2>Stats</h2>
        <p className="err">{err}</p>
        <div className="stack"><button className="primary" onClick={reload}>Try again</button></div>
      </div>
    );
  }
  if (!venues || !cats) return <div className="pad"><h2>Stats</h2><p className="muted">Loading...</p></div>;

  const observed = venues.reduce((a, v) => a + v.observed_seconds, 0);
  const sessions = venues.reduce((a, v) => a + v.sessions, 0);
  const tables = venues.reduce((a, v) => a + v.tables_counted, 0);
  const activeVenues = venues.filter((v) => v.sessions > 0).length;
  const withTime = cats.filter((c) => c.seconds > 0).sort((a, b) => b.seconds - a.seconds);

  return (
    <div className="pad">
      <h2>Stats</h2>
      <p className="muted">Completed blocks across every field observer. Updates as sessions sync.</p>

      <div className="kpis">
        <div className="kpi"><b>{formatDuration(observed)}</b><span>Observed</span></div>
        <div className="kpi"><b>{sessions}</b><span>Sessions</span></div>
        <div className="kpi"><b>{activeVenues} / {venues.length}</b><span>Venues observed</span></div>
        <div className="kpi"><b>{tables}</b><span>Tables counted</span></div>
      </div>

      {sessions === 0 ? (
        <div className="empty">
          <p><strong>No sessions recorded yet.</strong></p>
          <p className="muted">Numbers appear here once a field observer ends a session.</p>
        </div>
      ) : (
        <>
          <div className="card">
            <h3>By venue</h3>
            <ul className="venue-stats">
              {venues.map((v) => (
                <li className="venue-stat" key={v.venue_id}>
                  <div className="venue-stat-name">{v.venue_name}</div>
                  <small className="muted">Last observed {when(v.last_observed_at)}</small>
                  <div className="venue-stat-nums">
                    <div><b>{v.sessions}</b><span>Sessions</span></div>
                    <div><b>{v.observers}</b><span>Observers</span></div>
                    <div><b>{formatDuration(v.observed_seconds)}</b><span>Observed</span></div>
                    <div><b>{v.tables_counted}</b><span>Tables</span></div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="card">
            <h3>Time by activity</h3>
            <div className="bars">
              {withTime.map((c) => {
                const share = observed > 0 ? (c.seconds / observed) * 100 : 0;
                // The pilot's headline number: seconds per table, where the
                // opening tap counts as one table.
                const perTable = c.counts_tables && c.tables_counted > 0
                  ? ` · ${(c.seconds / c.tables_counted).toFixed(1)}s per table` : "";
                return (
                  <div className="bar-row" key={c.category_id}>
                    <span className="label">{c.label}</span>
                    <span className="value">
                      {share.toFixed(0)}% · {formatDuration(c.seconds)}{perTable}
                    </span>
                    <div className="bar-track"><div className="bar-fill" style={{ width: `${share}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div className="stack"><button onClick={reload}>Refresh</button></div>
    </div>
  );
}
